import React, { useRef } from 'react';
import { useObservable } from 'react-use-observable';

import { Button } from 'antd';

import isAfter from 'date-fns/isAfter';
import startOfDay from 'date-fns/startOfDay';
import subDays from 'date-fns/subDays';
import type { FieldProps } from 'formik';
import { Field, Form as FormikForm, useFormikContext } from 'formik';
import { SubscriptionCancelReasonType } from 'helpers/enums';
import { FilterActions } from 'v2/components/Pages/Reports/FilterActions';
import { FilterPanel } from 'v2/components/Pages/Reports/FilterPanel';
import { DateRangePickerFormField } from 'v2/components/Shared/FormFields/DateRangePicker';
import { SelectFormField } from 'v2/components/Shared/FormFields/SelectFormField';
import type { ISelectProductMultipleHandles } from 'v2/components/Shared/FormFields/SelectProduct';
import { SelectProduct } from 'v2/components/Shared/FormFields/SelectProduct';
import { TextFormField } from 'v2/components/Shared/FormFields/TextFormField';
import type { ICancelFilter } from 'v2/interfaces/reports/cancel';
import { CustomerSearchTypes } from 'v2/interfaces/reports/interruption';
import type { ICancelReason } from 'v2/interfaces/subscriptions';
import { initialFilter, suspendService } from 'v2/services/reports/suspend';
import SubscriptionsService from 'v2/services/subscriptions';

import { Icon } from '@eduzz/ui-antd-theme';

function Form() {
  const [isLoading] = useObservable(() => suspendService.isLoading(), []);
  const [cancelReason] = useObservable(
    () => SubscriptionsService.getSubscriptionCancelReasons(SubscriptionCancelReasonType.SUSPENSION),
    []
  );

  const { isValid, isSubmitting, resetForm, dirty, values, setFieldValue } = useFormikContext<ICancelFilter>();
  const selectProductRef = useRef<ISelectProductMultipleHandles>(null);

  const clientTypes = [
    { label: 'E-mail', value: CustomerSearchTypes.EMAIL },
    { label: 'Nome', value: CustomerSearchTypes.NAME },
    { label: 'CPF/CNPJ', value: CustomerSearchTypes.DOCUMENT }
  ];

  const customerStringOptions = {
    [CustomerSearchTypes.NAME]: { label: 'Nome', placeholder: 'Insira o nome' },
    [CustomerSearchTypes.EMAIL]: { label: 'E-mail', placeholder: 'Insira o e-mail' },
    [CustomerSearchTypes.DOCUMENT]: { label: 'CPF/CNPJ', placeholder: 'Insira o CPF/CNPJ' }
  };

  const onChangeClientType = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFieldValue('client', '');
    setFieldValue('customer_type', e.target.value);
  };

  const isOutsideRange = (day: Date) => isAfter(startOfDay(day), startOfDay(new Date()));

  const handleClean = () => {
    resetForm({ values: initialFilter });
    selectProductRef.current?.unCheckAll();
  };

  const motivesOptions = (cancelReason || []).map((item: ICancelReason) => ({
    label: item.name,
    value: item.id
  }));

  const loadingMotivesOptions = cancelReason === null;
  const customerLabel =
    customerStringOptions[!!values.customer_type ? values.customer_type : CustomerSearchTypes.EMAIL].label;
  const customerPlaceholder =
    customerStringOptions[!!values.customer_type ? values.customer_type : CustomerSearchTypes.EMAIL].placeholder;
  const customerMask = values.customer_type === CustomerSearchTypes.DOCUMENT ? 'document' : null;

  const today = startOfDay(new Date());
  const datePreset = [
    { id: 'today', text: 'Hoje', start: today, end: today },
    { id: 'week', text: 'Últimos 7 dias', start: subDays(today, 7), end: today },
    { id: '2weeks', text: 'Últimos 30 dias', start: subDays(today, 30), end: today },
    { id: 'month', text: 'Últimos 60 dias', start: subDays(today, 60), end: today }
  ];

  const isCleanDisabled = !dirty || isSubmitting || isLoading;
  const isFilterDisabled = !isValid || isSubmitting || isLoading;

  return (
    <FormikForm>
      <FilterPanel id='suspend-filter'>
        <div className='grid grid-cols-1 items-end gap-4 sm:grid-cols-2 xl:grid-cols-12'>
          <div className='xl:col-span-3'>
            <Field
              id='date'
              label='Período'
              name='date'
              component={DateRangePickerFormField}
              customPreset={datePreset}
              isOutsideRange={isOutsideRange}
              minDate={subDays(startOfDay(new Date()), 120)}
            />
          </div>
          <div className='xl:col-span-4'>
            <div className='grid grid-cols-3 gap-2'>
              <div>
                <Field
                  id='customer_type'
                  name='customer_type'
                  label='Cliente'
                  options={clientTypes}
                  onChange={onChangeClientType}
                  component={SelectFormField}
                  fullWidth
                />
              </div>
              <div className='col-span-2'>
                <Field
                  id='customer'
                  name='customer'
                  component={TextFormField}
                  label={customerLabel}
                  placeholder={customerPlaceholder}
                  mask={customerMask}
                  fullWidth
                />
              </div>
            </div>
          </div>
          <div className='xl:col-span-5'>
            <Field id='product_id' name='product_id'>
              {(props: FieldProps) => (
                <SelectProduct
                  single={false}
                  {...props}
                  label='Produtos'
                  endpoint='/reports/sales-filter-products'
                  ref={selectProductRef}
                />
              )}
            </Field>
          </div>
          <div className='md:col-span-1 xl:col-span-3'>
            <Field id='id' name='id' label='Contrato' mask='number' component={TextFormField} fullWidth />
          </div>
          <div className='md:col-span-1 xl:col-span-2'>
            <Field id='invoice_id' name='invoice_id' label='Fatura' mask='number' component={TextFormField} fullWidth />
          </div>
          <div className='md:col-span-1 xl:col-span-3'>
            <Field
              id='reason_id'
              name='reason_id'
              label='Motivo'
              loading={loadingMotivesOptions}
              options={motivesOptions}
              component={SelectFormField}
              multiple
              fullWidth
            />
          </div>
          <div className='xl:col-span-4'>
            <FilterActions>
              <Button id='clean-filter-button' onClick={handleClean} type='text' disabled={isCleanDisabled}>
                Limpar
              </Button>
              <Button
                id='filter-button'
                type='primary'
                disabled={isFilterDisabled}
                htmlType='submit'
                loading={isSubmitting || isLoading}
                icon={<Icon.Funnel />}
              >
                Filtrar
              </Button>
            </FilterActions>
          </div>
        </div>
      </FilterPanel>
    </FormikForm>
  );
}

export default Form;
